import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { programs } from '@/mocks/neurobaby';

const meditationSteps = [
  {
    id: 1,
    title: 'Llegá como estás',
    duration: '1 minuto',
    icon: 'ri-door-open-line',
    what: 'No hace falta que te prepares. No hace falta que cambiés nada. Sentate o acostate donde estés ahora mismo. Cerrá los ojos si podés. Si no podés, mirá un punto fijo. Solo respirá.',
    science: 'La neurociencia de la atención plena muestra que no necesitamos un estado mental "perfecto" para meditar. El acto de simplemente notar dónde estamos — sin juzgar, sin cambiar — activa la corteza prefrontal ventromedial, la zona del cerebro asociada con la autocompasión. No hay forma incorrecta de hacer esto. Tu presencia, tal como es, ya es suficiente.',
    action: 'Encontrá un lugar donde estés ahora. Una silla, el borde de la cama, el piso. Si tenés al bebé en brazos, abrazalo y respirá con él. Si está durmiendo, sentate al lado. No hay postura correcta. Solo estar.',
  },
  {
    id: 2,
    title: 'Reconocé lo que cargás',
    duration: '2 minutos',
    icon: 'ri-heart-3-line',
    what: 'Poné una mano en tu corazón. Sentí el peso de este día. No lo juzgues. No lo cambies. Solo reconocelo. Este cansancio tiene nombre: se llama amor.',
    science: 'La técnica de la "mano en el corazón" tiene base científica real. El contacto físico amable con el propio cuerpo activa las fibras C-táctiles, receptoras que envían señales directamente al sistema límbico y reducen la activación del eje HPA (hipotálamo-hipófisis-adrenal). En otras palabras: tocarte con ternura baja los niveles de cortisol. El simple acto de reconocer el cansancio sin pelear contra él es un acto de autorregulación neurobiológica.',
    action: 'Poné una mano sobre tu pecho. Notá el peso. Notá la tensión. Notá si hay algo más además del cansancio: miedo, rabia, tristeza, amor. Todo cabe. No tenés que solucionar nada. Solo ponéle nombre. Decilo en voz baja si podés. "Estoy cansada. Estoy asustada. Estoy aquí."',
  },
  {
    id: 3,
    title: 'Respiración 4-6',
    duration: '3 minutos',
    icon: 'ri-heart-pulse-line',
    what: 'Inhalá contando hasta 4. Exhalá contando hasta 6. Cada vez que exhalás, tu sistema nervioso parasimpático se activa. Tu corazón baja. Tu bebé lo siente. Hacelo 6 veces.',
    science: 'La respiración 4-6 (inhalación corta, exhalación larga) es una de las herramientas más estudiadas en neurociencia del estrés. Alargar la exhalación estimula directamente el nervio vago, el principal regulador del sistema parasimpático — la rama del sistema nervioso encargada del descanso, la digestión y la calma. Estudios de la Universidad de Stanford y el Laboratorio de Psicofisiología del Sueño demostraron que 6 ciclos de esta respiración reducen la frecuencia cardíaca en un promedio de 12 latidos por minuto. Tu bebé, a través de la co-regulación, siente esa bajada y su sistema nervioso se sincroniza con el tuyo.',
    action: 'Inhalá por la nariz contando 1-2-3-4. Exhalá por la boca contando 1-2-3-4-5-6. No forzés. Si te mareás, volvé a tu respiración normal y empezá de nuevo. No es una competencia. Cada exhalación es una señal que le enviás a tu cuerpo: "Estamos seguras. Podemos descansar."',
  },
  {
    id: 4,
    title: 'Una sola intención',
    duration: '2 minutos',
    icon: 'ri-sun-line',
    what: 'No una lista. No un plan. Solo una frase para vos. Puede ser: "Hoy fui suficiente." O simplemente: "Estoy acá." Eso es todo lo que hace falta.',
    science: 'Las intenciones simples —en contraste con las metas complejas— activan el sistema de recompensa del cerebro sin generar ansiedad de logro. El Dr. Judson Brewer, neurocientífico de la Universidad de Brown, demostró que las afirmaciones breves y centradas en el presente reducen la activación de la amígdala (centro del miedo) más efectivamente que las visualizaciones elaboradas. Una sola frase, dicha en voz baja, ancla la mente en lo que ya es, en lugar de lo que debería ser. Y en esa ancla, descansa.',
    action: 'Elegí una frase. Cualquiera. "Hoy fui suficiente." "Estoy acá." "Mi amor no necesita ser perfecto." "Esta noche, descanso." Repetila 3 veces, en voz baja o en tu mente. No tiene que ser brillante. Tiene que ser verdadera. Eso es todo. Eso es todo lo que hace falta.',
  },
];

export default function MeditacionMamasCansadas() {
  const [activeStep, setActiveStep] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-sand/40">
        <div
          className="h-full bg-terracotta transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 md:pt-28 pb-8 md:pb-14 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-center mb-6 md:mb-10">
            <div className="w-full lg:w-1/2">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-block bg-[#AAB7A3] text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('meditacion.tag1')}
                </span>
                <span className="inline-block bg-terracotta text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('meditacion.tag2')}
                </span>
                <span className="inline-block bg-sand text-[#3D2B1F] text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('meditacion.tag3')}
                </span>
              </div>
              <h1 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold text-[#8B6F5C] leading-tight mb-4">
                {t('meditacion.title1')} {t('meditacion.title2')}
              </h1>
              <p className="font-sans text-sm md:text-base text-[#3D2B1F] leading-relaxed">
                No hace falta que tengas 20 minutos. No hace falta que lo hagas perfecto.
                Solo hace falta que respires.
              </p>
            </div>
            <div className="w-full lg:w-1/2 flex items-center justify-center">
              <img
                src="https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/a311d4f1-1c52-40c3-8319-5a8c55de83a0_compressed_ChatGPT-Image-11-ago-2026-10_04_52-p.m..webp"
                alt="Mamá meditando con velas"
                className="w-full max-w-[270px] md:max-w-[336px] h-auto rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Introducción Emocional */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="w-full max-w-3xl mx-auto">
          <div className="bg-cream rounded-3xl p-8 md:p-12 border-l-4 border-terracotta">
            <p className="font-sans text-lg md:text-xl text-[#3D2B1F] leading-relaxed mb-6">
              Si cuando escuchás la palabra <em>meditación</em> pensás que no es para vos,
              quedate un minuto.
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              Pensás que no tenés tiempo. Que no podés cerrar los ojos porque tenés miedo de que
              algo le pase al bebé. Que tu cabeza no para nunca.
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              Esta meditación no es para las que tienen todo bajo control.
              <strong> Es para las que no tienen nada bajo control y siguen adelante igual.</strong>
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              Para las que lloran en la ducha. Para las que sonríen aunque estén rotas por dentro.
              Para las que a las 3 de la mañana se preguntan si lo están haciendo bien.
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
              <strong>Esta es para ellas.</strong>
            </p>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed">
              Y si estás acá, en esta página, a esta hora, en este momento exacto de tu vida —
              esta es para vos.
            </p>
          </div>
        </div>
      </section>

      {/* Frase Poética */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-6">
            <i className="ri-moon-line text-terracotta text-2xl" />
          </div>
          <p className="font-serif text-xl md:text-2xl text-[#3D2B1F] leading-relaxed mb-4">
            "No necesitás vaciarte para meditar.
          </p>
          <p className="font-serif text-xl md:text-2xl text-[#3D2B1F] leading-relaxed mb-4">
            Necesitás quedarte quieta un momento con todo lo que sos.
          </p>
          <p className="font-serif text-xl md:text-2xl text-[#3D2B1F] leading-relaxed mb-4">
            Con el cansancio, con el amor, con el miedo, con la fuerza.
          </p>
          <p className="font-serif text-xl md:text-2xl text-[#3D2B1F] leading-relaxed mb-4">
            Todo eso junto sos vos.
          </p>
          <p className="font-serif text-xl md:text-2xl text-[#3D2B1F] leading-relaxed">
            <strong>Y eso es exactamente suficiente.</strong>
          </p>
        </div>
      </section>

      {/* Práctica Guiada — 4 Pasos */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('meditacion.practica_tag')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('meditacion.practica_title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              No hace falta que sigas el orden. No hace falta que hagas todos.
              Empezá por el que resuene con vos en este momento.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-8 md:mb-12 justify-center">
            {meditationSteps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(index)}
                className={`inline-flex items-center gap-2 font-sans text-sm font-semibold px-5 py-3 rounded-full transition-all duration-300 whitespace-nowrap ${
                  activeStep === index
                    ? 'bg-terracotta text-white shadow-sm'
                    : 'bg-white text-[#3D2B1F] hover:bg-sand/50 border border-sand/40'
                }`}
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 text-xs font-bold">
                  {step.id}
                </span>
                {step.title}
              </button>
            ))}
          </div>

          {/* Active Step Content */}
          <div className="bg-cream rounded-3xl p-6 md:p-10 border border-sand/40">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#AAB7A3]/15 text-[#AAB7A3]">
                <i className={`${meditationSteps[activeStep].icon} text-xl`} />
              </div>
              <div>
                <span className="font-sans text-xs font-semibold text-terracotta uppercase tracking-wider">
                  {t('meditacion.paso')} {meditationSteps[activeStep].id} de 4 · {meditationSteps[activeStep].duration}
                </span>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#8B6F5C] leading-tight">
                  {meditationSteps[activeStep].title}
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <div className="md:col-span-1">
                <div className="bg-white rounded-2xl p-6 h-full">
                  <span className="inline-block font-sans text-xs font-semibold text-[#5C4A3A] uppercase tracking-wider mb-3">
                    {t('meditacion.que_hacer')}
                  </span>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed">
                    {meditationSteps[activeStep].what}
                  </p>
                </div>
              </div>
              <div className="md:col-span-1">
                <div className="bg-white rounded-2xl p-6 h-full">
                  <span className="inline-block font-sans text-xs font-semibold text-[#5C4A3A] uppercase tracking-wider mb-3">
                    {t('meditacion.ciencia_detras')}
                  </span>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed">
                    {meditationSteps[activeStep].science}
                  </p>
                </div>
              </div>
              <div className="md:col-span-1">
                <div className="bg-terracotta/10 rounded-2xl p-6 h-full border border-terracotta/20">
                  <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-wider mb-3">
                    {t('meditacion.empeza_ahora')}
                  </span>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed font-medium">
                    {meditationSteps[activeStep].action}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation arrows */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-sand/30">
              <button
                onClick={() => setActiveStep((prev) => (prev > 0 ? prev - 1 : meditationSteps.length - 1))}
                className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
              >
                <i className="ri-arrow-left-line" />
                {t('meditacion.paso_anterior')}
              </button>
              <span className="font-sans text-xs text-[#5C4A3A]">
                {activeStep + 1} / {meditationSteps.length}
              </span>
              <button
                onClick={() => setActiveStep((prev) => (prev < meditationSteps.length - 1 ? prev + 1 : 0))}
                className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
              >
                {t('meditacion.paso_siguiente')}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Cierre Emocional Profundo */}
      <section className="py-4 md:py-6 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-left">
          <div className="w-10 h-10 flex items-center justify-center mb-4">
            <i className="ri-star-line text-cream text-2xl" />
          </div>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed mb-2">
            "El agotamiento que sentís no es debilidad. Es el precio del amor más grande que existe.
          </p>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed mb-2">
            Estás sosteniendo a un ser humano que está aprendiendo a existir en el mundo.
          </p>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed mb-2">
            Eso es lo más difícil y lo más sagrado que hay.
          </p>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed mb-2">
            Y lo estás haciendo sola, en la oscuridad, sin que nadie te aplauda.
          </p>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed">
            Pero tu bebé lo sabe. Lo siente en cada célula. Vos sos su mundo entero. Y ese mundo, hoy, le está enseñando que las tormentas pasan."
          </p>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed mb-2 mt-6">
            Hay una soledad que no se explica. No es la soledad de estar sin gente. Es la soledad de ser la única que sabe exactamente cuánto cuesta cada día. La única que conoce cada llanto, cada señal, cada miedo. Esa soledad es el precio de amar sin límites.
          </p>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed mb-2">
            Pero esta noche, en este momento, no estás sola. Hay millones de mamás en este mismo instante, con los ojos cerrados o abiertos, con el bebé dormido o despierto, preguntándose lo mismo que vos: ¿estoy siendo suficiente?
          </p>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed mb-2">
            <strong>La respuesta es sí. Siempre fue sí.</strong>
          </p>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed mb-2">
            Lo esencial es invisible a los ojos. Y lo que vos hacés cada día, ese amor que no descansa, que no pide permiso, que no necesita aplausos — ese amor ya está cambiando el mundo.
          </p>
          <p className="font-serif text-base md:text-lg text-cream leading-relaxed">
            El mundo pequeño y enorme que duerme en tus brazos.
          </p>
        </div>
      </section>

      {/* CTA al Programa Completo */}
      <section className="py-10 md:py-16 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-2">
              {t('tutorial.next_step')}
            </span>
            <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#8B6F5C] leading-tight mb-3">
              Esto que sentiste es solo una gota
            </h2>
            <p className="font-sans text-sm md:text-base text-[#3D2B1F] max-w-2xl mx-auto">
              El Programa Completo NeuroBabys incluye meditaciones guiadas de 5, 10 y 20 minutos para cada etapa de la maternidad, y una comunidad que te acompaña.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {programs.map((program) => (
              <div
                key={program.id}
                className="group bg-white rounded-2xl overflow-hidden border border-sand/40 transition-all duration-300 hover:border-terracotta/40 flex flex-col"
              >
                <div className="h-40 overflow-hidden relative">
                  <img
                    src={program.image}
                    alt={t(`programs.items.${program.id}.title`, program.title)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="font-sans text-xs font-semibold text-[#8B6F5C]">
                      {t(`programs.items.${program.id}.level`, program.level)}
                    </span>
                  </div>
                  {'badge' in program && (program as any).badge && (
                    <div className="absolute top-3 right-3 bg-terracotta text-white px-3 py-1 rounded-full">
                      <span className="font-sans text-xs font-semibold">{(program as any).badge}</span>
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <span className="font-sans text-xs text-[#5C4A3A] mb-1">
                    {t(`programs.items.${program.id}.subtitle`, program.subtitle)}
                  </span>
                  <h3 className="font-serif text-lg md:text-xl font-bold text-[#8B6F5C] mb-2 leading-tight">
                    {t(`programs.items.${program.id}.title`, program.title)}
                  </h3>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed mb-4 flex-grow">
                    {t(`programs.items.${program.id}.description`, program.description)}
                  </p>
                  <div className="flex items-center gap-1.5 mb-4 text-xs text-[#5C4A3A]">
                    <i className="ri-book-open-line text-xs" />
                    <span>{program.lessons} {t('common.guias')}</span>
                  </div>
                  <div className="pt-4 border-t border-sand/30">
                    <div>
                      <span className="font-sans text-2xl font-bold text-[#8B6F5C]">${program.price}</span>
                      <span className="font-sans text-xs text-[#5C4A3A] ml-1">{t('common.usd')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}